import React, { useState } from 'react'
import { useTheme } from 'styled-components'
import Link from '@/components/Link'
import Icon from '@caterdesk/ui--icon'
import { main, mobileMediaQuery } from '@caterdesk/ui--theme'
import { amountToPriceString } from '@caterdesk/utils--money'
import { useTranslation } from 'react-i18next'
import { Text, TextExtraSmall, TextSmall } from '@caterdesk/ui--typography'
import {
  BackLink,
  Badges,
  Content,
  Keywords,
  Details,
  Header,
  Logo,
  Title,
  Veil,
  ModalLogo,
  BoxHeader,
  HandpickedBox,
  SourcingBox,
  MobileImage,
  ImageContainer,
  BlackVeil,
  ModalHeader,
  Info,
} from './index.styles'
import { useMedia } from 'react-use'
import { Currency } from '@/generated/graphql'
import {
  Button,
  Chip,
  Typography,
  Stack,
  useTheme as useMuiTheme,
  Link as MuiLink,
  Dialog,
  DialogContent,
  Box,
} from '@mui/material'
import { InfoRounded, KeyboardArrowLeftRounded, Star, Warning } from '@mui/icons-material'
import useTenant from '@/hooks/useTenant'
import { VendorExternalUrlData } from '@/generated/gql/graphql'
import { getTenantLinkComponentRule } from '@/helpers/tenants'
import { getImageUrl } from '../image'

export type Props = {
  images: string[]
  logo?: string | null
  title: string
  keywords?: string | null
  descriptionFull?: string | null
  quote?: string | null
  sourcing?: string | null
  rating?: number | null
  totalRatings?: number | null
  currency: Currency
  minimumOrderValueExTax?: number | null
  minimumNoticePeriod?: number | null
  taxAcronym: string
  externalUrlData: VendorExternalUrlData
}

const VendorHeader: React.FC<Props> = ({
  images,
  logo,
  title,
  keywords,
  descriptionFull = '',
  quote,
  sourcing,
  rating,
  totalRatings,
  currency,
  minimumOrderValueExTax,
  minimumNoticePeriod,
  taxAcronym,
  externalUrlData,
}) => {
  const theme = useTheme()
  const muiTheme = useMuiTheme()
  const tenant = useTenant()
  const { t } = useTranslation()
  const allVendorsLink = getTenantLinkComponentRule(
    '/office-catering/vendors',
    tenant.domain,
    false,
    false,
  )
  const minOrderVal = minimumOrderValueExTax
  const [modalOpen, setModalOpen] = useState(false)
  const isMobileScreen = useMedia(mobileMediaQuery, false)

  const infoUrlData = externalUrlData.data.find((url) => url?.tags.data.includes('default'))
  const infoUrl = infoUrlData?.url || ''
  const infoCopy = infoUrlData?.copy || ''

  const backgroundImage = images[0] || null

  return (
    <>
      <Header image={images[0]} backgroundSize="50% auto">
        <Veil>
          {allVendorsLink.displayComponent && (
            <BackLink>
              <Link href={allVendorsLink.uri}>
                <Button size="small" startIcon={<KeyboardArrowLeftRounded />}>
                  {t('marketplace:backToAllVendors')}
                </Button>
              </Link>
            </BackLink>
          )}
          <Content>
            {isMobileScreen && (
              <ImageContainer>
                <BlackVeil />
                <MobileImage src={images[0]} alt="vendor" />
              </ImageContainer>
            )}
            <Details>
              {logo && (
                <Logo
                  src={getImageUrl({
                    src: logo,
                    width: 200,
                    quality: 100,
                  })}
                />
              )}
              <Info>
                <Title>{title}</Title>
                <div>
                  {keywords && <Keywords>{keywords}</Keywords>}
                  <Button
                    onClick={() => setModalOpen(true)}
                    size="small"
                    startIcon={<InfoRounded />}
                  >
                    {t('marketplace:moreInfo')}
                  </Button>
                </div>
                <Badges>
                  {allVendorsLink.displayComponent && rating && Number(rating) >= 2 ? (
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                      <Chip label={`${rating}/5`} color="info" icon={<Star />} />
                      <Typography>
                        {totalRatings
                          ? `(${totalRatings} ${totalRatings > 1 ? t('marketplace:ratings') : t('marketplace:rating')})`
                          : ''}
                      </Typography>
                    </Stack>
                  ) : null}
                  {minOrderVal &&
                    (isMobileScreen ? (
                      <TextExtraSmall style={{ fontWeight: 500 }}>{`${amountToPriceString(
                        currency,
                        minOrderVal,
                        true,
                      )} ${t('marketplace:minimum')}`}</TextExtraSmall>
                    ) : (
                      <Text style={{ fontWeight: 500 }}>
                        {`${amountToPriceString(currency, minOrderVal, true)} ${t('marketplace:minimum')}`}{' '}
                        ({t('marketplace:ex', { taxAcronym })})
                      </Text>
                    ))}
                </Badges>

                {minimumNoticePeriod && allVendorsLink.displayComponent ? (
                  <Stack spacing={1} direction="row" marginTop={1} alignItems="center">
                    <Chip
                      color="info"
                      label="!"
                      size="small"
                      sx={{ minWidth: '27px', minHeight: '27px' }}
                      variant="outlined"
                    />
                    <Stack alignItems="center" direction="row" flexWrap="wrap">
                      <Typography variant="caption" color="text.disabled">
                        {t('marketplace:ordersMustBePlaced')}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="info.main"
                        sx={{ margin: '0 0.3rem 0 0.3rem' }}
                      >
                        {minimumNoticePeriod} {t('marketplace:hours')}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {t('marketplace:inAdvance')}
                      </Typography>
                    </Stack>
                  </Stack>
                ) : null}

                {infoCopy && infoUrl ? (
                  <Stack
                    marginTop={0.5}
                    spacing={1}
                    direction="row"
                    alignContent="center"
                    alignItems="center"
                  >
                    <Warning fontSize="medium" htmlColor={muiTheme.palette.secondary.light} />
                    <Typography variant="body2" color="text.disabled">
                      {infoCopy} [
                      <MuiLink href={infoUrl} target="_blank">
                        {t('marketplace:linkHere')}
                      </MuiLink>
                      ]
                    </Typography>
                  </Stack>
                ) : null}
              </Info>
            </Details>
          </Content>
        </Veil>
      </Header>

      <div style={{ display: modalOpen ? 'flex' : 'none' }}>
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
              },
            }}
          >
            <Box
              sx={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <DialogContent>
              <ModalHeader>
                {logo && <ModalLogo src={getImageUrl({ src: logo, width: 50, quality: 100 })} />}
                {title}
              </ModalHeader>

              <Stack direction="column" spacing={2} mt={2}>
                <TextSmall>{descriptionFull}</TextSmall>
                {quote && (
                  <HandpickedBox>
                    <BoxHeader>
                      <Icon icon="heart" fill={main.colors.error} variation="circle" size={17} />
                      {t('marketplace:handpickedBy', { tenantName: tenant.name })}
                    </BoxHeader>
                    {quote}
                  </HandpickedBox>
                )}
                {sourcing && (
                  <SourcingBox>
                    <BoxHeader>
                      <Icon
                        icon="sourced"
                        fill={theme.colors.primary}
                        variation="circle"
                        size={17}
                      />
                      {t('marketplace:sourcedResponsibly')}
                    </BoxHeader>
                    {sourcing}
                  </SourcingBox>
                )}
              </Stack>
            </DialogContent>
          </Box>
        </Dialog>
      </div>
    </>
  )
}

export default VendorHeader
